import { Injectable, OnDestroy, HostListener } from '@angular/core';
import _ from 'lodash';
import { environment } from 'src/environments/environment';
import { io, Socket, ManagerOptions } from 'socket.io-client'
import { throwError as observableThrowError, BehaviorSubject, Observable, Subscription, Subject } from 'rxjs';
import { Stream } from 'src/app/models/streams';
import { Angle, Clip } from '../models/clip';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { NotificationService } from 'src/app/services/notification.service';
import { User } from '../models/user';
import { Conversation, Message } from '../models/chat';
import { CameraStorageSnapshot, QwikcutCamera } from '../models/qwikcut-camera';
import { BreakdownRequest } from '../models/breakdowns';
import { OutreachCampaign, OutreachCampaignMessage } from '../models/outreach';
import { GameStatPatch, GameStatsCollaborationScope, RemoteGameStatsActiveCell } from './gamestats.service';
import { OdPrelabelUpdate } from '../models/ml-object-detection';

const STATISTICIAN_STATUS_ROOM_NAME = 'statistician-online-status';

export interface GameStatsCollaborator {
  userid: string;
  firstname: string;
  lastname: string;
  socketid?: string;
  color?: string;
}

export interface GameStatsPresenceEvent {
  teamid?: string | null;
  gameid: string;
  collaborationScope?: GameStatsCollaborationScope;
  users: GameStatsCollaborator[];
}

export interface GameStatsChangedEvent {
  teamid?: string | null;
  gameid: string;
  collaborationScope?: GameStatsCollaborationScope;
  statPatches: GameStatPatch[];
  sourceClientSaveId?: string;
  sourceUserid?: string;
  sourceUser?: {_id: string; firstname: string; lastname: string};
}

export interface GameStatsActiveCellsEvent {
  teamid?: string | null;
  gameid: string;
  collaborationScope?: GameStatsCollaborationScope;
  cells: RemoteGameStatsActiveCell[];
}

export interface MlTrimmingJobProgressEvent {
  jobId: string;
  gameid: string;
  angle: Angle;
  modelVersion: string;
  status: 'queued' | 'submitting' | 'processing' | 'complete' | 'partial_failure' | 'failed' | 'superseded';
  clipCount: number;
  processedCount: number;
  completedCount: number;
  successfulCount: number;
  failedCount: number;
  pendingCount: number;
  active: boolean;
  priority?: number;
  source?: 'breakdown' | 'evaluation';
  writeClipTrims?: boolean;
  evaluationids?: string[];
  completedAt?: Date;
  updatedAt: Date;
}

export interface AnalystTaskRequestUpdatedEvent {
  userid: string;
  taskRequestDate: Date | string | null;
  analystName?: string;
}

export interface BreakdownRequestChangedEvent {
  requestIds: string[];
  operation: 'created' | 'updated' | 'removed';
  changedFields: string[];
  occurredAt: Date | string;
}

export interface StatisticianStatusEvent {
	status: 'Online' | 'Offline' | 'Idle' | 'Active';
	online?: boolean;
	hasStartedWork?: boolean;
	idle?: boolean;
	idleSince?: Date | string;
	idleDueAt?: Date | string;
	idleTime?: Date;
	presenceRevision?: number;
	userid: string;
	occurredAt?: Date | string;
}

export interface AnalystNotificationEvent {
  type: 'assigned' | 'reassigned' | 'unassigned' | 'message';
  title: string;
  message: string;
  breakdownRequestId?: string;
  gameid?: string;
  assignmentSlot?: string;
  createdAt: Date | string;
}

export interface MlTrimmingSettingsEvent {
  key: 'global';
  breakdownAutoSubmitEnabled: boolean;
  revision: number;
  updatedByUserid?: string;
  updatedByName?: string;
  updatedAt?: Date;
}

@Injectable({
  providedIn: 'root',
})
export class SocketIOService implements OnDestroy {
  static socket: Socket;
	static idleTimeout: any;
	static isIdle: boolean = false;
  userUpdateSub: Subscription;
  lastUser: User;
  connectionIsActive: boolean = true;
  private mlTrimmingAdminRoomRequested = false;
  private mlObjectDetectionAdminRoomRequested = false;
  private analystTaskRequestRoomRequested = false;
  private analystNotificationRoomRequested = false;
  private statDashboardBreakdownRoomRequested = false;
  private statisticianStatusRoomRequested = false;
  private statisticianStatusUserid: string = null;

  private activeConnectionChange = new Subject<boolean>();
  private socketConnectionChange = new Subject<boolean>();
  private statisticianConnectionReady = new BehaviorSubject<boolean>(false);

  constructor(
    private localStorageService: LocalStorageService
  ) {
    this.setupSocketIo();
  }
  // @HostListener('window:beforeunload')
  ngOnDestroy(): void {
    if (this.userUpdateSub) this.userUpdateSub.unsubscribe();
    if (SocketIOService.socket) {
			SocketIOService.socket.disconnect();
      SocketIOService.socket = null;
    }
  }

  getSocketUrl(): string {
    return environment.socketUrl;
  }

  setupSocketIo() {
		if (SocketIOService.socket?.active && SocketIOService.socket?.id) return;

    SocketIOService.socket = io(this.getSocketUrl(), {
      transports: ['websocket'],
    });

    SocketIOService.socket.on('connect', () => {
		this.statisticianConnectionReady.next(false);
      const user = this.localStorageService.getUser();
			this.processUserConnection(user);
      if (this.mlTrimmingAdminRoomRequested) this.emitMlTrimmingAdminRoomJoin();
      if (this.mlObjectDetectionAdminRoomRequested) this.emitMlObjectDetectionAdminRoomJoin();
      if (this.analystTaskRequestRoomRequested) this.emitAnalystTaskRequestRoomJoin();
      if (this.analystNotificationRoomRequested) this.emitAnalystNotificationRoomJoin();
      if (this.statDashboardBreakdownRoomRequested) this.emitStatDashboardBreakdownRoomJoin();
      if (this.statisticianStatusRoomRequested) this.emitStatisticianStatusRoomJoin();
      this.socketConnectionChange.next(true);
    });

    SocketIOService.socket.on('disconnect', () => {
      this.statisticianConnectionReady.next(false);
      this.socketConnectionChange.next(false);
    });

    this.userUpdateSub = this.localStorageService
      .listenForUserUpdate()
      .subscribe({
        next: (user) => {
					this.processUserConnection(user);
        },
      });

		this.onAppInteraction();
  }

	processUserConnection(user: User) {
		if (user?.currentUserRole?.role === 'fan') {
			this.listenForActiveConnections(user);
			this.emitConnectionEvent(user);
		}
		else if (_.includes(['statsapi', 'statmanager'], user?.currentUserRole?.role)) {
			this.emitStatisticianConnectionEvent(user);
		}
		else if (this.lastUser?.currentUserRole?.role) {
			if (this.lastUser.currentUserRole.role === 'fan') {
        this.emit('userDisconnect', this.lastUser._id);
			}
			else if (_.includes(['statsapi', 'statmanager'], this.lastUser.currentUserRole.role)) {
        this.emit('statisticianDisconnect', this.lastUser._id);
			}
		}
		this.lastUser = user;
	}

  listenForChatMessage(roomName: string) {
    return this.listen(roomName);
  }

  joinRoom(conversationId: string, userId: string) {
    SocketIOService.socket.emit('joinRoom', conversationId, userId);
  }

  leaveRoom(conversationId: string, userId: string) {
    SocketIOService.socket.emit('leaveRoom', conversationId, userId);
  }

  joinRoomLiveStream(gameid: string, userid: string) {
    SocketIOService.socket.emit('joinRoomLiveStream', gameid, userid);
  }

  leaveRoomLiveStream(gameid: string, userid: string) {
    SocketIOService.socket.emit('leaveRoomLiveStream', gameid, userid);
  }

  joinGameStats(teamid: string | null, gameid: string, userid: string, collaborationScope: GameStatsCollaborationScope = 'team') {
    SocketIOService.socket.emit('join-game-stats', {teamid, gameid, userid, collaborationScope});
  }

  leaveGameStats(teamid: string | null, gameid: string, userid: string, collaborationScope: GameStatsCollaborationScope = 'team') {
    SocketIOService.socket.emit('leave-game-stats', {teamid, gameid, userid, collaborationScope});
  }

  setGameStatsActiveCell(teamid: string | null, gameid: string, userid: string, clipnumber?: number, columnName?: string, collaborationScope: GameStatsCollaborationScope = 'team') {
    SocketIOService.socket.emit('game-stats-active-cell', {teamid, gameid, userid, clipnumber, columnName, collaborationScope});
  }

	listenForStatisticianStatus(userid: string): Observable<StatisticianStatusEvent> {
		this.statisticianStatusRoomRequested = true;
		this.statisticianStatusUserid = userid;
		this.emitStatisticianStatusRoomJoin();
		return this.listen('statistician-status');
	}

	private emitStatisticianStatusRoomJoin() {
		if (!this.statisticianStatusRoomRequested || !this.statisticianStatusUserid) return;
		this.emit('join-statistican-status', this.statisticianStatusUserid);
	}

  enterAssignedGame(userid: string, gameid: string) {
    this.emit('statisticianGameEnter', {userid, gameid, source: 'game-entry'});
  }

  markAssignedGameActive(userid: string, gameid: string) {
    this.emit('statisticianGameActive', {userid, gameid});
  }

	reportAssignedGameActivity(userid: string, gameid: string, source: 'playback' | 'progress' | 'reconnect' = 'progress') {
		this.emit('statisticianGameActivity', {userid, gameid, source});
	}

  leaveAssignedGame(userid: string, gameid: string, inProgress: boolean) {
    this.emit('statisticianGameLeave', {userid, gameid, inProgress});
  }

  enterAnalystPage(userid: string) {
    this.emit('statisticianPageEnter', userid);
  }

  leaveAnalystPage(userid: string) {
    this.emit('statisticianPageLeave', userid);
  }

	stopListeningForStatisticianStatus(userid: string) {
		this.statisticianStatusRoomRequested = false;
		this.statisticianStatusUserid = null;
		this.emit('leave-statistican-status', userid);
	}

  listenForActiveConnections(user: User) {
    if (!SocketIOService.socket) return;
    if (this.lastUser) SocketIOService.socket.off(`activeConnections-${this.lastUser._id}`);

    if (!user) return;
    SocketIOService.socket.on(`activeConnections-${user._id}`, (socketids: string[]) => {
      this.connectionIsActive = _.includes(socketids, SocketIOService.socket.id);
      this.emitActiveConnectionChange();
    });
  }

  emitConnectionEvent(user: User) {
    if (user) {
      this.emit('userConnect', user._id);
    }
    if (this.lastUser) {
      if (this.lastUser._id !== user?._id) {
        this.emit('userDisconnect', this.lastUser._id);
      }
    }
  }
	
	emitStatisticianConnectionEvent(user: User) {
    if (user) {
      SocketIOService.socket?.emit(
        'statisticianConnect',
        {token: this.localStorageService.getToken()},
        (result: {success: boolean}) => this.statisticianConnectionReady.next(!!result?.success)
      );
    }
    if (this.lastUser) {
      if (this.lastUser._id !== user?._id) {
        this.emit('statisticianDisconnect', this.lastUser._id);
      }
    }
	}

	onAppInteraction() {
		const user = this.localStorageService.getUser();
		if (!_.includes(['statsapi', 'statmanager'], user?.currentUserRole?.role)) return;
		// console.log('App Interaction');
		if (SocketIOService.isIdle) {
      this.emit('statisticianActive', user._id);
		}
		SocketIOService.isIdle = false;
		if (SocketIOService.idleTimeout) {
			clearTimeout(SocketIOService.idleTimeout);
			SocketIOService.idleTimeout = null;
		}
		SocketIOService.idleTimeout = setTimeout(() => {
			console.log('User is idle!');
			SocketIOService.isIdle = true;
      this.emit('statisticianIdle', user._id);
		}, 1000 * 60 * 5); //5 mins
	}

  activateConnection() {
		const user = this.localStorageService.getUser();
		if (!user?._id) return;
    this.emit('activateConnection', user._id);
  }

  listenForActiveConnectionChange(): Observable<boolean> {
    return this.activeConnectionChange.asObservable();
  }

  emitActiveConnectionChange() {
    this.activeConnectionChange.next(this.connectionIsActive);
  }

  emit(eventName: string, data?: any) {
    if (!SocketIOService.socket) return;
    SocketIOService.socket.emit(eventName, data);
  }

  listen(eventName: string): Observable<any> {
    if (!SocketIOService.socket) return null;
    return new Observable((observer) => {
      const handler = (data: any) => {
        observer.next(data);
      };
      SocketIOService.socket.on(eventName, handler);
      return () => {
				SocketIOService.socket?.off(eventName, handler);
      };
    });
  }

  listenForGameClips(gameid: string): Observable<Clip> {
    return this.listen(`${gameid}-clips`);
  }

  listenForExchangeHudlImport(exchangeRequestId: string): Observable<any> {
    return this.listen(`${exchangeRequestId}-hudl-import`);
  }

  listenForExchangePlaylistImport(exchangeRequestId: string): Observable<any> {
    return this.listen(`${exchangeRequestId}-playlist-import`);
  }

  listenForGameStreamEvents(gameid: string): Observable<Stream> {
    return this.listen(`${gameid}-stream`);
  }

  listenForGameStatsPresence(): Observable<GameStatsPresenceEvent> {
    return this.listen('game-stats-presence');
  }

  listenForGameStatsChanges(): Observable<GameStatsChangedEvent> {
    return this.listen('game-stats-changed');
  }

  listenForGameStatsActiveCells(): Observable<GameStatsActiveCellsEvent> {
    return this.listen('game-stats-active-cells');
  }

  listenForMergeProgress(gameid: string): Observable<{gameid: string, angle: Angle, clipnumber: number, mergeProgress: number | null}> {
    return this.listen(`${gameid}-merge-progress`);
  }

  joinMlTrimmingAdminRoom() {
    this.mlTrimmingAdminRoomRequested = true;
    this.emitMlTrimmingAdminRoomJoin();
  }

  leaveMlTrimmingAdminRoom() {
    this.mlTrimmingAdminRoomRequested = false;
    this.emit('leave-admin-ml-trimming');
  }

  listenForMlTrimmingJobUpdates(): Observable<MlTrimmingJobProgressEvent> {
    return this.listen('admin-ml-trimming-job-updated');
  }

  listenForMlTrimmingSettingsUpdates(): Observable<MlTrimmingSettingsEvent> {
    return this.listen('admin-ml-trimming-settings-updated');
  }

  joinMlObjectDetectionAdminRoom() {
    this.mlObjectDetectionAdminRoomRequested = true;
    this.emitMlObjectDetectionAdminRoomJoin();
  }

  leaveMlObjectDetectionAdminRoom() {
    this.mlObjectDetectionAdminRoomRequested = false;
    this.emit('leave-admin-ml-object-detection');
  }

  listenForOdPrelabelUpdates(): Observable<OdPrelabelUpdate> {
    return this.listen('admin-ml-object-detection-prelabel-updated');
  }

  joinAnalystTaskRequestRoom() {
    this.analystTaskRequestRoomRequested = true;
    this.emitAnalystTaskRequestRoomJoin();
  }

  leaveAnalystTaskRequestRoom() {
    this.analystTaskRequestRoomRequested = false;
    this.emit('leave-analyst-task-requests');
  }

  listenForAnalystTaskRequestUpdates(): Observable<AnalystTaskRequestUpdatedEvent> {
    return this.listen('analyst-task-request-updated');
  }

  joinStatDashboardBreakdownRoom() {
    this.statDashboardBreakdownRoomRequested = true;
    this.emitStatDashboardBreakdownRoomJoin();
  }

  leaveStatDashboardBreakdownRoom() {
    this.statDashboardBreakdownRoomRequested = false;
    this.emit('leave-stat-dashboard-breakdowns');
  }

  listenForBreakdownRequestChanges(): Observable<BreakdownRequestChangedEvent> {
    return this.listen('breakdown-request-changed');
  }

  listenForSocketConnectionChanges(): Observable<boolean> {
    return this.socketConnectionChange.asObservable();
  }

  listenForStatisticianConnectionReady(): Observable<boolean> {
    return this.statisticianConnectionReady.asObservable();
  }

  joinAnalystNotificationRoom() {
    this.analystNotificationRoomRequested = true;
    this.emitAnalystNotificationRoomJoin();
  }

  leaveAnalystNotificationRoom() {
    this.analystNotificationRoomRequested = false;
    this.emit('leave-analyst-notifications');
  }

  listenForAnalystNotifications(): Observable<AnalystNotificationEvent> {
    return this.listen('analyst-notification');
  }

  private emitMlTrimmingAdminRoomJoin() {
    const token = this.localStorageService.getToken();
    if (token) this.emit('join-admin-ml-trimming', {token});
  }

  private emitMlObjectDetectionAdminRoomJoin() {
    const token = this.localStorageService.getToken();
    if (token) this.emit('join-admin-ml-object-detection', {token});
  }

  private emitAnalystTaskRequestRoomJoin() {
    const token = this.localStorageService.getToken();
    if (token) this.emit('join-analyst-task-requests', {token});
  }

  private emitAnalystNotificationRoomJoin() {
    const token = this.localStorageService.getToken();
    if (token) this.emit('join-analyst-notifications', {token});
  }

  private emitStatDashboardBreakdownRoomJoin() {
    const token = this.localStorageService.getToken();
    if (token) this.emit('join-stat-dashboard-breakdowns', {token});
  }

  listenForUpdatedUser(userid: string): Observable<User> {
    return this.listen(`${userid}-user-update`);
  }

  getGameCaptureEventName(gameid: string): string {
    return `${gameid}-game-capture`;
  }

  listenForGameCapture(
    gameid: string
  ): Observable<{ clipnumber: number; angle: Angle; starting: boolean }> {
    return this.listen(this.getGameCaptureEventName(gameid));
  }

  listenForNewMessage(conversationid: string): Observable<{
    text: string;
    sender: string;
    type: string;
    conversationid: string;
    id: string;
  }> {
    return this.listen(`${conversationid}-message`);
  }

	emitGameCaptureConnect(gameid: string, angle: Angle) {
    SocketIOService.socket.emit('gameCaptureConnect', gameid, angle);
	}

	emitGameCaptureDisconnect() {
    SocketIOService.socket.emit('gameCaptureDisconnect');
	}

  listenForGameCaptureConnect(gameid: string): Observable<{gameid: string, angle: Angle, socketid: string, disconnect: boolean}> {
    return this.listen(`gameCaptureConnect-${gameid}`);
  }

  listenForCameraUpdate(cameraId: string): Observable<QwikcutCamera> {
    return this.listen(`camera-update-${cameraId}`);
  }

  listenForCameraStorageUpdate(cameraId: string): Observable<CameraStorageSnapshot> {
    return this.listen(`camera-storage-update-${cameraId}`);
  }

  listenForNewBreakdownRequests(): Observable<BreakdownRequest> {
    return this.listen(`new-breakdown-requests`);
  }

  listenForOutreachCampaignUpdates(): Observable<{campaignid: string, campaign: OutreachCampaign}> {
    return this.listen('outreach-campaign-updated');
  }

  listenForOutreachCampaignMessages(): Observable<{campaignid: string, message: OutreachCampaignMessage, isInboundReply: boolean}> {
    return this.listen('outreach-campaign-message');
  }
}
